import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider, useSiteSettings } from "@/context/LanguageContext";
import { WishlistProvider } from "@/context/WishlistContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ChatWidget } from "@/components/ChatWidget";
import { CookieBanner } from "@/components/CookieBanner";
import { Analytics } from "@/components/Analytics";
import { ExitPopup } from "@/components/ExitPopup";
import { ThemeApplier } from "@/components/ThemeApplier";
import Home from "@/pages/Home";
import NotFound from "@/pages/NotFound";
import { ISLAND_PATH, ISLAND_BY_PATH, islandUrl, propertyUrl, langFromPath, langPrefix, PAGE_PATHS } from "@/lib/translations";
import { RouteSeo } from "@/lib/useSeo";
import { legacyTarget } from "@/lib/legacyUrls";
import { ScrollToTop } from "@/lib/useScrollToTop";
import { api } from "@/lib/api";
import { lazy, Suspense, useEffect, useState } from "react";

// Objektliste/-detail/Inseln werden nach dem Start vorgeladen (prefetch), damit die Startseite schlank bleibt und der Wechsel trotzdem sofort ist
const Properties = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Properties"));
const PropertyDetail = lazy(() => import(/* webpackPrefetch: true */ "@/pages/PropertyDetail"));
const IslandPage = lazy(() => import(/* webpackPrefetch: true */ "@/pages/IslandPage"));
const PlacePage = lazy(() => import("@/pages/PlacePage"));
const Wishlist = lazy(() => import("@/pages/Wishlist"));
// Code-Splitting: Admin, Portale, Buchung & Nebenseiten laden erst bei Aufruf (Hauptbundle klein → schnellerer Start der Startseite)
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const FaqPage = lazy(() => import("@/pages/FaqPage"));
const Contact = lazy(() => import("@/pages/Contact"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminMailPage = lazy(() => import("@/pages/AdminMailPage"));
const LandlordLogin = lazy(() => import("@/pages/LandlordLogin"));
const CustomerLogin = lazy(() => import("@/pages/CustomerLogin"));
const CustomerReset = lazy(() => import("@/pages/CustomerLogin").then((m) => ({ default: m.CustomerReset })));
const CustomerPortal = lazy(() => import("@/pages/CustomerPortal"));
const LandlordInvite = lazy(() => import("@/pages/LandlordInvite"));
const LandlordAction = lazy(() => import("@/pages/LandlordAction"));
const LandlordPortal = lazy(() => import("@/pages/LandlordPortal"));
const PortalAdminEntry = lazy(() => import("@/pages/PortalAdminEntry"));
const CustomerAdminEntry = lazy(() => import("@/pages/CustomerAdminEntry"));
const ComplexPage = lazy(() => import("@/pages/ComplexPage"));
const ComplexesPage = lazy(() => import("@/pages/ComplexesPage"));
const TipsPage = lazy(() => import("@/pages/TipsPage"));
const LandlordSitePage = lazy(() => import("@/pages/LandlordSitePage"));
const LandlordSitesIndexPage = lazy(() => import("@/pages/LandlordSitesIndexPage"));
const NewsletterStatus = lazy(() => import("@/pages/NewsletterStatus"));

const IslandRedirect = () => {
  const { path } = useParams();
  return <Navigate to={`/${ISLAND_PATH[path] || path}`} replace />;
};

const ExternalRedirect = ({ to }) => {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
};


const LegacyPropertyRedirect = () => {
  const { slug } = useParams();
  const [target, setTarget] = useState(null);
  useEffect(() => {
    api.get(`/properties/${slug}`).then((r) => setTarget(propertyUrl(r.data) + window.location.search)).catch(() => setTarget("/properties"));
  }, [slug]);
  return target ? <Navigate to={target} replace /> : null;
};

// Alte .html-Adressen und Nicht-Kanaren-Pfade der alten Website umleiten, bevor die Seite lädt
const LegacyGuard = ({ children }) => {
  const { pathname } = useLocation();
  const target = legacyTarget(pathname);
  if (target && target !== pathname) return <Navigate to={target + window.location.search} replace />;
  return children;
};

// /insel/ort/irgendwas (alte Unterseiten) → echte Weiterleitung auf die Inselseite statt 404/noindex
const SubGone = () => {
  const { path } = useParams();
  const island = ISLAND_BY_PATH[path];
  useEffect(() => { if (island) window.location.replace(islandUrl(island) + window.location.search); }, [island]);
  return island ? <div className="min-h-screen" aria-busy="true" /> : <NotFound />;
};

function Layout({ children }) {
  const gaId = useSiteSettings().ga_measurement_id || "";
  return (
    <>
      <ThemeApplier />
      <Header />
      <main><Suspense fallback={<div className="min-h-screen" aria-busy="true" />}>{children}</Suspense></main>
      <Footer />
      <ChatWidget />
      <CookieBanner analyticsEnabled={!!gaId} />
      <Analytics measurementId={gaId} />
      <ExitPopup />
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <WishlistProvider>
      <div className="App">
        <BrowserRouter basename={langPrefix(langFromPath())}>
          <RouteSeo lang={langFromPath()} />
          {/* ExternalRedirect: /sitemap.xml → Backend-Sitemap (Browser/Bots, die die statische Datei erwarten) */}
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/sitemap.xml" element={<ExternalRedirect to="/api/sitemap.xml" />} />
            <Route path="/sitemap-images.xml" element={<ExternalRedirect to="/api/sitemap-images.xml" />} />
            <Route path="/properties" element={<Layout><Properties /></Layout>} />
            <Route path="/fincas-anlagen" element={<Layout><ComplexesPage /></Layout>} />
            <Route path="/reisetipps" element={<Layout><TipsPage /></Layout>} />
            {/* Vermieter-Homepage: eigener Look ohne Kopf-/Fußzeile der Hauptseite */}
            <Route path="/homepage/:slug" element={<><ThemeApplier /><Suspense fallback={<div className="min-h-screen" aria-busy="true" />}><LandlordSitePage /></Suspense><CookieBanner analyticsEnabled={false} /></>} />
            <Route path="/homepages/:domain" element={<><ThemeApplier /><Suspense fallback={<div className="min-h-screen" aria-busy="true" />}><LandlordSitesIndexPage /></Suspense><CookieBanner analyticsEnabled={false} /></>} />
            <Route path="/buchen/:slug" element={<Layout><BookingPage /></Layout>} />
            <Route path="/property/:slug" element={<LegacyPropertyRedirect />} />
            <Route path="/insel/:path" element={<IslandRedirect />} />
            <Route path="/newsletter" element={<Layout><NewsletterStatus /></Layout>} />
            <Route path="/impressum" element={<Layout><LegalPage slug="impressum" /></Layout>} />
            {[...new Set(Object.values(PAGE_PATHS.bewertungen))].map((p) => <Route key={p} path={`/${p}`} element={<Layout><ReviewsPage /></Layout>} />)}
            <Route path="/faq" element={<Layout><FaqPage /></Layout>} />
            <Route path="/:lang/faq" element={<Layout><FaqPage /></Layout>} />
            <Route path="/kontakt" element={<Layout><Contact /></Layout>} />
            <Route path="/datenschutz" element={<Layout><LegalPage slug="datenschutz" /></Layout>} />
            <Route path="/vermittlungsbedingungen" element={<Layout><LegalPage slug="vermittlungsbedingungen" /></Layout>} />
            <Route path="/:path" element={<LegacyGuard><Layout><IslandPage /></Layout></LegacyGuard>} />
            <Route path="/:path/finca/:slug" element={<Layout><ComplexPage /></Layout>} />
            <Route path="/:path/anlage/:slug" element={<Layout><ComplexPage /></Layout>} />
            <Route path="/:path/:place" element={<LegacyGuard><Layout><PlacePage /></Layout></LegacyGuard>} />
            <Route path="/:path/:place/:sub" element={<LegacyGuard><Layout><SubGone /></Layout></LegacyGuard>} />
            <Route path="/admin/login" element={<Layout><AdminLogin /></Layout>} />
            <Route path="/admin" element={<Layout><AdminDashboard /></Layout>} />
            <Route path="/admin/mail" element={<Layout><AdminMailPage /></Layout>} />
            <Route path="/vermieter/login" element={<Layout><LandlordLogin /></Layout>} />
            <Route path="/kunde/login" element={<Layout><CustomerLogin /></Layout>} />
            <Route path="/kunde/passwort/:token" element={<Layout><CustomerReset /></Layout>} />
            <Route path="/kunde" element={<Layout><CustomerPortal /></Layout>} />
            <Route path="/kunde/admin-ansicht" element={<Layout><CustomerAdminEntry /></Layout>} />
            <Route path="/merkliste" element={<Layout><Wishlist /></Layout>} />
            <Route path="/vermieter/einladung/:token" element={<Layout><LandlordInvite /></Layout>} />
            <Route path="/vermieter/aktion/:token" element={<Layout><LandlordAction /></Layout>} />
            <Route path="/vermieter/passwort-zuruecksetzen/:token" element={<Layout><LandlordInvite reset /></Layout>} />
            <Route path="/vermieter/admin-ansicht" element={<Layout><PortalAdminEntry /></Layout>} />
            <Route path="/vermieter" element={<Layout><LandlordPortal /></Layout>} />
            <Route path="*" element={<Layout><NotFound /></Layout>} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
      </WishlistProvider>
    </LanguageProvider>
  );
}

export default App;
